import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { switchMap, map, catchError, withLatestFrom } from 'rxjs/operators';
import { of, Observable } from 'rxjs';
import { MwSuppliersApiService } from 'app/services/suppliers-api.service';
import * as suppliersActions from '../actions/suppliers.actions';
import * as notifyActions from 'app/store/general/actions/notify.actions';
import { Store } from '@ngrx/store';
import { getAddressesState } from '../reducers';
import * as addressesActions from '../actions/addresses.actions';
import { SupplierApiModel } from 'app/models/suppliers.model';
import { AddressFormatter } from 'app/components/map/map.models';
@Injectable()
export class SuppliersEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<any>,
    private suppliersApiService: MwSuppliersApiService
  ) {}

  loadSuppliers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(suppliersActions.loadSuppliers),
      switchMap(({ loadOptions }) => {
        return this.suppliersApiService.load(loadOptions).pipe(
          // Add formatted address
          map((response) => {
            response.results.map((address) => {
              address.formattedAddress = AddressFormatter.format(address);
              return address;
            });
            return response;
          }),
          map((response) => {
            return suppliersActions.loadSuppliersSuccess({
              response,
            });
          }),
          catchError((error) =>
            of(suppliersActions.loadSuppliersFailure({ error }))
          )
        );
      })
    );
  });

  createSuppliers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(suppliersActions.createSupplier),
      withLatestFrom(this.store$.select(getAddressesState)),
      switchMap(([{ supplier }, state]) => {
        return this.suppliersApiService
          .create({
            ...supplier,
            addressId: state.savedAddress ? state.savedAddress.addressId : null,
          })
          .pipe(
            switchMap(() => [
              suppliersActions.createSupplierSuccess(),
              notifyActions.notifySuccess({
                notification: {
                  phrase: 'OPERATION.add_supplier_successful',
                },
              }),
              addressesActions.resetAddress(),
            ]),
            catchError((error) =>
              of(suppliersActions.createSupplierFailure({ error }))
            )
          );
      })
    )
  );

  updateSuppliers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(suppliersActions.updateSupplier),
      withLatestFrom(this.store$.select(getAddressesState)),
      switchMap(([{ supplier }, state]) => {
        let resultObservable: Observable<SupplierApiModel>;
        if (state.savedAddress && !supplier.addressId) {
          resultObservable = this.suppliersApiService.update({
            ...supplier,
            addressId: state.savedAddress ? state.savedAddress.addressId : null,
          });
        } else {
          resultObservable = this.suppliersApiService.update(supplier);
        }
        return resultObservable.pipe(
          switchMap(() => [
            suppliersActions.updateSupplierSuccess(),
            notifyActions.notifySuccess({
              notification: {
                phrase: 'OPERATION.update_supplier_successful',
              },
            }),
          ]),
          catchError((error) =>
            of(suppliersActions.updateSupplierFailure({ error }))
          )
        );
      })
    )
  );

  deleteSuppliers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(suppliersActions.deleteSupplier),
      switchMap(({ supplier }) => {
        return this.suppliersApiService.delete(supplier).pipe(
          switchMap(() => [
            suppliersActions.deleteSupplierSuccess(),
            notifyActions.notifySuccess({
              notification: {
                phrase: 'OPERATION.delete_supplier_successful',
              },
            }),
          ]),
          catchError((error) =>
            of(suppliersActions.deleteSupplierFailure({ error }))
          )
        );
      })
    )
  );
}
